<template>
  <div class="quick-search">
    <button ref="trigger" type="button" class="search-trigger" aria-haspopup="dialog" :aria-expanded="open ? 'true' : 'false'" @click="show">
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></svg>
      <span class="trigger-text">جستجوی صفحه یا شماره ویزیت…</span>
      <kbd class="trigger-key" dir="ltr" aria-hidden="true">Ctrl K</kbd>
    </button>

    <div v-if="open" class="search-layer" @mousedown.self="hide">
      <div class="search-panel" role="dialog" aria-modal="true" aria-label="جستجوی سریع" @keydown.esc.prevent="hide">
        <div class="search-field">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></svg>
          <input ref="input" v-model="query" type="search" role="combobox" autocomplete="off" spellcheck="false"
                 placeholder="نام صفحه، بخش یا شماره ویزیت را بنویسید" aria-label="جستجو" aria-controls="quick-search-list"
                 :aria-expanded="results.length ? 'true' : 'false'" :aria-activedescendant="results.length ? 'quick-option-' + active : null"
                 @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="go(results[active])">
          <kbd dir="ltr" aria-hidden="true">Esc</kbd>
        </div>
        <div class="search-body">
          <p v-if="results.length" class="group-label">{{ query.trim() ? 'نتایج' : recentShown ? 'اخیراً بازدیدشده' : 'پیشنهاد' }}</p>
          <ul v-if="results.length" id="quick-search-list" role="listbox" aria-label="نتایج جستجو">
            <li v-for="(item, index) in results" :id="'quick-option-' + index" :key="item.key" role="option" :aria-selected="index === active ? 'true' : 'false'"
                class="option" :class="{ active: index === active }" @mousemove="active = index" @click="go(item)">
              <span class="option-icon" :class="{ visit: item.visit }">
                <svg v-if="item.visit" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3.5h6v3H9zM9 5H6v15.5h12V5h-3M9.5 13l2 2 3.5-3.5" /></svg>
                <NavigationIcon v-else :route="item.path" />
              </span>
              <span class="option-copy"><strong>{{ item.title }}</strong><small v-if="item.trail.length">{{ item.trail.join(' ‹ ') }}</small></span>
              <span v-if="index === active" class="option-go" aria-hidden="true">↵</span>
            </li>
          </ul>
          <div v-else class="no-results" role="status">
            <EmptyState kind="search" size="sm" title="صفحه‌ای پیدا نشد" description="عبارت دیگری را امتحان کنید یا شماره ویزیت را وارد کنید." />
          </div>
        </div>
        <footer class="search-foot" aria-hidden="true"><span><kbd>↑</kbd><kbd>↓</kbd> پیمایش</span><span><kbd>↵</kbd> باز کردن</span><span><kbd>Esc</kbd> بستن</span></footer>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationIcon from '../components/NavigationIcon/index.vue';
import EmptyState from '../components/EmptyState/index.vue';
import { searchableEntries } from '../utils/adminNavigation';
import { rankEntries, visitNumber } from '../utils/searchText';

const RECENT_KEY = 'vino-admin-recent-pages';
const MAX_RECENT = 5;

export default {
  name: 'QuickSearch',
  components: { NavigationIcon, EmptyState },
  data: () => ({ open: false, query: '', active: 0, recent: [] }),
  computed: {
    entries() {
      return searchableEntries(this.$STORE.state.appConfig.menu).map((entry) => ({
        key: entry.id, title: entry.title, trail: entry.trail, path: entry.path,
      }));
    },
    canOpenVisit() { return this.entries.some((entry) => entry.path.toLowerCase().startsWith('/visitmanagment')); },
    recentShown() { return !this.query.trim() && this.recentEntries.length > 0; },
    recentEntries() {
      return this.recent.map((path) => this.entries.find((entry) => entry.path === path)).filter(Boolean);
    },
    results() {
      if (!this.query.trim()) return (this.recentEntries.length ? this.recentEntries : this.entries).slice(0, 7);
      const ranked = rankEntries(this.entries, this.query, 8);
      const id = visitNumber(this.query);
      if (id !== null && this.canOpenVisit) {
        ranked.unshift({ key: 'visit-' + id, visit: true, title: `ویزیت شماره ${id.toLocaleString('fa-IR', { useGrouping: false })}`,
          trail: ['باز کردن گزارش ویزیت'], path: '/visitmanagment/answerlist/' + id });
      }
      return ranked;
    },
  },
  watch: { query() { this.active = 0; }, $route() { this.open = false; } },
  created() { this.loadRecent(); },
  mounted() { document.addEventListener('keydown', this.onKey); },
  beforeDestroy() { document.removeEventListener('keydown', this.onKey); },
  methods: {
    onKey(event) {
      const typing = /^(input|textarea|select)$/i.test((event.target || {}).tagName || '') || (event.target || {}).isContentEditable;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); this.open ? this.hide() : this.show(); }
      else if (event.key === '/' && !typing && !this.open) { event.preventDefault(); this.show(); }
    },
    show() {
      this.open = true; this.query = ''; this.active = 0; this.loadRecent();
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    hide() { this.open = false; this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus()); },
    move(step) {
      const count = this.results.length;
      if (!count) return;
      this.active = (this.active + step + count) % count;
      this.$nextTick(() => { const el = document.getElementById('quick-option-' + this.active); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' }); });
    },
    go(item) {
      if (!item) return;
      this.remember(item);
      this.open = false;
      this.$router.push(item.path, () => {}, () => {});
    },
    loadRecent() {
      try { this.recent = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((path) => typeof path === 'string'); } catch (_) { this.recent = []; }
    },
    remember(item) {
      if (item.visit) return; // a visit number is not a page
      const next = [item.path, ...this.recent.filter((path) => path !== item.path)].slice(0, MAX_RECENT);
      this.recent = next;
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (_) { /* private mode: recents just do not persist */ }
    },
  },
};
</script>

<style scoped>
.quick-search { flex: 1 1 280px; min-width: 0; max-width: 440px; }
/* trigger sits on the navy topbar */
.search-trigger { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 42px; padding: 0 12px; border: 1px solid var(--shell-line); border-radius: var(--r-sm);
  background: var(--shell-bg-2); color: var(--shell-text-3); font: inherit; font-size: 13px; cursor: pointer; transition: border-color .15s, background .15s, color .15s; }
.search-trigger:hover { background: var(--shell-raised); border-color: rgba(205, 216, 255, .24); color: var(--shell-text-2); }
.search-trigger:focus-visible { outline: 2px solid var(--amber-500) !important; outline-offset: 2px; }
.search-trigger svg, .search-field svg { width: 19px; height: 19px; flex: none; fill: none; stroke: currentColor; stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }
.trigger-text { flex: 1; min-width: 0; text-align: right; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
kbd { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 22px; padding: 0 6px; border: 1px solid var(--line-strong); border-bottom-width: 2px; border-radius: var(--r-xs);
  background: var(--surface); color: var(--text-3); font: 600 10.5px/1 var(--mono); }
.trigger-key { background: transparent; border-color: var(--shell-line); color: var(--shell-text-3); }
.search-layer { position: fixed; inset: 0; z-index: 2000; display: flex; justify-content: center; align-items: flex-start; padding: 11vh 16px 16px; background: rgba(1, 7, 34, .62); backdrop-filter: blur(3px); }
.search-panel { position: relative; width: min(640px, 100%); max-height: 78vh; display: flex; flex-direction: column; border-radius: var(--r-md); background: var(--surface); box-shadow: 8px 8px 0 var(--navy-950), var(--shadow-pop); overflow: hidden; animation: pop .16s var(--ease-out); }
.search-panel::before { content: ''; height: 3px; flex: none; background: var(--bars); }
@keyframes pop { from { opacity: 0; transform: translateY(-8px) scale(.985); } to { opacity: 1; transform: none; } }
.search-field { display: flex; align-items: center; gap: 12px; padding: 0 18px; min-height: 60px; border-bottom: 1px solid var(--line); color: var(--teal-700); }
.search-field input { flex: 1; min-width: 0; height: 58px; border: 0; background: transparent; color: var(--text); font: inherit; font-size: 15px; outline: none; }
.search-panel .search-field input, .search-panel .search-field input:focus { border: 0; box-shadow: none; outline: none; background: transparent; border-radius: 0; }
.search-field input::placeholder { color: var(--text-4); }
.search-field input::-webkit-search-cancel-button { display: none; }
.search-body { overflow-y: auto; padding: 8px 10px 10px; min-height: 120px; }
.group-label { margin: 6px 10px; color: var(--text-4); font-size: 11px; font-weight: 800; }
ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
.option { position: relative; display: flex; align-items: center; gap: 12px; padding: 9px 10px; border-radius: var(--r-sm); cursor: pointer; }
.option.active { background: var(--navy-50); }
.option.active::before { content: ''; position: absolute; inset-inline-start: 0; top: 10px; bottom: 10px; width: 4px; background: var(--pink-500); transform: skewY(var(--slant)); }
.option-icon { display: grid; place-items: center; width: 38px; height: 38px; flex: none; border-radius: var(--r-sm); background: var(--navy-900); color: var(--teal-300); }
.option-icon.visit { background: var(--amber-500); color: var(--navy-900); }
.option-icon .navigation-icon { width: 20px; height: 20px; flex-basis: 20px; }
.option-icon.visit svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.option-copy { display: grid; flex: 1; min-width: 0; line-height: 1.5; }
.option-copy strong { font-size: 13.5px; color: var(--text); font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.option-copy small { font-size: 11.5px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.option-go { color: var(--pink-700); font-weight: 800; padding: 0 6px; }
.no-results { padding: 4px 0 8px; }
.search-foot { display: flex; flex-wrap: wrap; gap: 6px 18px; padding: 10px 18px; border-top: 1px solid var(--line); background: var(--surface-sunken); color: var(--text-3); font-size: 11.5px; }
.search-foot span { display: inline-flex; align-items: center; gap: 5px; }
@media (max-width: 860px) {
  .quick-search { flex: 0 0 auto; }
  .search-trigger { width: 42px; padding: 0; justify-content: center; }
  .trigger-text, .trigger-key { display: none; }
  .search-layer { padding-top: 6vh; }
  .search-foot { display: none; }
}
@media (prefers-reduced-motion: reduce) { .search-panel { animation: none; } }
</style>
